import { useCallback } from 'react';

// eslint-disable-next-line import/order
import { useReactFlow } from '@xyflow/react';
import { flextree } from 'd3-flextree';
import { stratify } from 'd3-hierarchy';
import { useNotify } from 'react-admin';

import { flowchartLogger } from 'src/lib/logger';

import type { FlowChartNodeData } from '../dataProvider/types';
import type { Node, Edge } from '@xyflow/react';
import type { HierarchyNode } from 'd3-hierarchy';

const OFFSET_SIZE = 10;
const OFFSET_RESET_COUNT = 10;

interface NodeWithExitOrder extends Node<FlowChartNodeData> {
  exitOrder?: number;
  childCount?: number;
}

/**
 * Auto position nodes as a horizontal tree with improved exit ordering
 */
const useAutoLayout: () => () => void = () => {
  const { setNodes, getNodes, getEdges, getZoom } = useReactFlow();
  const notify = useNotify();

  const getExitOrder = useCallback(
    (sourceHandle: string | null | undefined, sourceNodeId: string): number => {
      if (sourceHandle === null || sourceHandle === undefined) return 0;

      // Handle different types of sourceHandles
      if (sourceHandle === 'simple') return 0;
      if (sourceHandle === 'default_connection-source') return 1000; // Place default connections at the end
      if (sourceHandle === 'true_connection') return 1;
      if (sourceHandle === 'false_connection') return 2;
      if (sourceHandle === 'available_connection') return 3;
      if (sourceHandle === 'away_connection') return 4;
      if (sourceHandle === 'busy_connection') return 5;
      if (sourceHandle === 'timeout_connection') return 6;
      if (sourceHandle === 'unreachable_connection') return 7;

      // For UUID-based handles, find the index in the node's exit arrays
      const sourceNode = getNodes().find((n) => n.id === sourceNodeId);
      if (sourceNode !== undefined && 'data' in sourceNode) {
        const nodeData = sourceNode.data as FlowChartNodeData;

        // Check matchRules (router nodes)
        if (nodeData.matchRules !== undefined && nodeData.matchRules !== null) {
          const matchRuleIndex = nodeData.matchRules.findIndex((rule: { id: string }) => {
            return rule.id === sourceHandle;
          });
          if (matchRuleIndex !== -1) {
            return matchRuleIndex; // Use array index as exit order
          }
        }

        // Check selections (IVR nodes)
        if (nodeData.selections !== undefined && nodeData.selections !== null) {
          const selectionIndex = nodeData.selections.findIndex((selection: { id: string }) => {
            return selection.id === sourceHandle;
          });
          if (selectionIndex !== -1) {
            return selectionIndex; // Use array index as exit order
          }
        }

        // Check instructions (Queue and User nodes)
        if (
          nodeData.instructions !== undefined &&
          nodeData.instructions !== null &&
          Array.isArray(nodeData.instructions)
        ) {
          const instructionIndex = nodeData.instructions.findIndex((instruction: { id: string }) => {
            return instruction.id === sourceHandle;
          });
          if (instructionIndex !== -1) {
            return instructionIndex; // Use array index as exit order
          }
        }
      }

      // Unknown handle type - should not happen with current node types
      flowchartLogger.warn(`Unknown source handle type: ${sourceHandle} for node ${sourceNodeId}`);
      return 999;
    },
    [getNodes]
  );

  const buildOrderedHierarchy = useCallback(
    (nodes: Array<Node<FlowChartNodeData>>, edges: Edge[]): NodeWithExitOrder[] => {
      // Create a map of parent -> children with exit order
      const childrenMap = new Map<string, Array<{ node: Node<FlowChartNodeData>; exitOrder: number }>>();

      edges.forEach((edge) => {
        const exitOrder = getExitOrder(edge.sourceHandle, edge.source);
        if (!childrenMap.has(edge.source)) {
          childrenMap.set(edge.source, []);
        }
        const children = childrenMap.get(edge.source);
        const targetNode = nodes.find((n) => n.id === edge.target);
        if (children && targetNode) {
          children.push({
            node: targetNode,
            exitOrder,
          });
        }
      });

      // Sort children by exit order for each parent
      childrenMap.forEach((children, parentId) => {
        children.sort((a, b) => a.exitOrder - b.exitOrder);
        childrenMap.set(parentId, children);
      });

      // Add exit order and child count to nodes
      return nodes.map((node) => {
        const children = childrenMap.get(node.id) ?? [];
        return {
          ...node,
          exitOrder: 0, // Will be set by parent
          childCount: children.length,
        };
      });
    },
    [getExitOrder]
  );

  const getExitOffset = useCallback(
    (edge: Edge, parentNode: Node<FlowChartNodeData>): number => {
      // For router nodes with 4+ exits, add a small offset based on exit order
      if (
        (parentNode.type === 'router' || parentNode.type === 'custom_router') &&
        parentNode.data?.matchRules !== undefined &&
        parentNode.data.matchRules !== null &&
        parentNode.data.matchRules.length >= 4
      ) {
        const exitOrder = getExitOrder(edge.sourceHandle, edge.source);
        // 10px offset per exit, reset every 10th exit to prevent infinite offsetting
        return (exitOrder % OFFSET_RESET_COUNT) * OFFSET_SIZE;
      }
      return 0;
    },
    [getExitOrder]
  );

  return useCallback((): void => {
    const nodes = getNodes();
    const edges = getEdges();
    const zoom = getZoom();
    if (nodes.length === 0) return;

    const entryNode = nodes[0];
    if (!entryNode) return;
    const entryNodeEl = document.querySelector(`[data-id="${entryNode.id}"]`);
    if (!entryNodeEl) return;

    try {
      const orderedNodes = buildOrderedHierarchy(nodes, edges);

      // Adjust this value to change the spacing between nodes
      const spacing = 100;

      // Create a custom layout that respects exit ordering
      // Use the plain tree node as the generic parameter for flextree
      const layout = flextree<PlainTreeNode>({
        nodeSize: (node) => {
          if (node?.data !== undefined) {
            const data = node.data;
            const nodeEl = document.querySelector(`[data-id="${data.id}"]`);
            if (nodeEl !== null) {
              const { width, height } = nodeEl.getBoundingClientRect();
              return [height / zoom, width / zoom];
            }

            // Calculate height for nodes with variable exits when DOM element is not available
            if (data.type === 'router' || data.type === 'custom_router') {
              const matchRulesCount = data.data?.matchRules?.length ?? 0;
              const calculatedHeight = 148 + (matchRulesCount > 0 ? (matchRulesCount - 1) * 24 : 0);
              return [calculatedHeight / zoom, 200 / zoom];
            }

            if (data.type === 'ivr') {
              const selectionsCount = data.data?.selections?.length ?? 1;
              const calculatedHeight = 92 + (selectionsCount > 1 ? (selectionsCount - 1) * 24 : 0);
              return [calculatedHeight / zoom, 200 / zoom];
            }

            // Mapping nodes have fixed height (single exit)
            if (data.type === 'mapping') {
              return [92 / zoom, 200 / zoom];
            }
          }
          return [200, 200];
        },
        // Unfortunately it doesn't adjust the spacing between rows, only adjacent nodes
        spacing,
      });

      // Build hierarchy with ordered children
      const hierarchy = stratify<NodeWithExitOrder>()
        .id((node) => node.id)
        .parentId((node) => {
          // Find parent by looking at edges, but maintain exit order
          const parentEdge = edges.find((edge) => edge.target === node.id);
          return parentEdge?.source;
        });

      const root: HierarchyNode<NodeWithExitOrder> = hierarchy(orderedNodes);

      // Sort children by exit order before layout
      const sortChildrenByExitOrder = (node: HierarchyNode<NodeWithExitOrder>): void => {
        if (node.children !== undefined) {
          node.children.sort((a, b) => {
            const aEdge = edges.find((e) => e.target === a.data.id);
            const bEdge = edges.find((e) => e.target === b.data.id);
            const aOrder = getExitOrder(aEdge?.sourceHandle, aEdge?.source ?? '');
            const bOrder = getExitOrder(bEdge?.sourceHandle, bEdge?.source ?? '');
            return aOrder - bOrder;
          });
          node.children.forEach(sortChildrenByExitOrder);
        }
      };

      sortChildrenByExitOrder(root);

      // Convert d3 hierarchy to plain data tree to avoid circular refs in .data
      type PlainTreeNode = NodeWithExitOrder & { children?: PlainTreeNode[] };
      const toPlain = (n: HierarchyNode<NodeWithExitOrder>): PlainTreeNode => {
        const children = n.children?.map(toPlain);
        const data: PlainTreeNode = { ...n.data };
        return children !== undefined && children.length > 0 ? { ...data, children } : data;
      };

      const plainRoot: PlainTreeNode = toPlain(root);
      const tree = layout.hierarchy(plainRoot);
      const generatedLayout = layout(tree);

      const sortedNodes: Array<Node<FlowChartNodeData>> = generatedLayout.descendants().map((node) => {
        const spacingX = spacing * node.depth;

        // Add exit offset for router children to prevent merging
        let xOffset = 0;
        if (node.parent) {
          const parentNode = nodes.find((n) => n.id === node.parent?.data.id);
          const edge = edges.find((e) => e.target === node.data.id);
          if (parentNode && edge) {
            xOffset = getExitOffset(edge, parentNode);
          }
        }

        const { children, ...nodeData } = node.data;
        return {
          ...nodeData,
          position: {
            x: node.y + spacingX + xOffset,
            y: node.x,
          },
        };
      });

      setNodes(sortedNodes);
    } catch (error) {
      flowchartLogger.error('Failed to auto layout', error);
      if (error instanceof Error && error.message === 'multiple roots') {
        notify('callflows.notifications.errorSortingMultipleRoots', { type: 'error' });
      } else {
        notify('callflows.notifications.errorSorting', { type: 'error' });
      }
    }
  }, [getNodes, getEdges, getZoom, setNodes, notify, buildOrderedHierarchy, getExitOrder, getExitOffset]);
};

export default useAutoLayout;
